import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import { useMediaQuery } from "react-responsive";

import {
  ApiErrorModel,
  TransactionBulkCardValues,
  TransactionBulkSharedValues,
} from "@interfaces/index";
import {
  QUERY_WIDTHS,
  BULK_TRANSACTIONS_MAX,
  getClashingDescriptionIds,
  getSubcategoryById,
  getAmountLimitViolation,
} from "@utils/index";
import { useTransactionBulkCreate } from "@providers/index";
import { useNotify } from "@hooks/index";
import {
  FormRow,
  Button,
  PlusSvg,
  HardMaxAmountModal,
  SoftAmountLimitModal,
  TransactionBulkCard,
} from "@components/index";

import {
  TransactionBulkCardApi,
  TransactionBulkCardErrors,
} from "./TransactionBulkCard";

interface BulkCard {
  id: string;
}

type AmountLimitConfirmations = { softMax?: boolean; softMin?: boolean };

export interface TransactionBulkDetailsHandle {
  submit: () => void;
}

let bulkCardCounter = 0;

const createBulkCard = (): BulkCard => ({
  id: `bulk-card-${++bulkCardCounter}`,
});

const getCardDescriptions = (
  cards: BulkCard[],
  values: Record<string, TransactionBulkCardValues>,
) =>
  cards.map(({ id }) => ({ id, description: values[id]?.description || "" }));

const LIMIT_ERRORS = {
  hardMax: "This exceeds the maximum value for this transaction type.",
  softMax: "This amount appears higher than we'd expect to see.",
  softMin: "This amount appears lower than we'd expect to see.",
};

// Splits "transactions.1.amount" / "transactions.1.files.0.uuid" errors by card
const getCardErrorsByIndex = (fields: Record<string, string> = {}) => {
  const errorsByIndex: Record<number, TransactionBulkCardErrors> = {};
  Object.entries(fields).forEach(([key, message]) => {
    const match = key.match(/^transactions\.(\d+)\.(amount|description|files)/);
    if (!match || !message) {
      return;
    }
    const index = Number(match[1]);
    const field = match[2] as keyof TransactionBulkCardErrors;
    errorsByIndex[index] = { ...errorsByIndex[index], [field]: message };
  });
  return errorsByIndex;
};

/**
 * "Transaction details" section in bulk mode: a growing list of cards saved
 * together in one request. Mounted only while bulk mode is on, so leaving it
 * resets the cards. The form's Save button calls `submit` through the ref.
 */
export const TransactionBulkDetails = forwardRef<
  TransactionBulkDetailsHandle,
  {
    subcategory?: ReturnType<typeof getSubcategoryById>;
    onValidateShared: () => Promise<TransactionBulkSharedValues | undefined>;
    onSaveError: (error: ApiErrorModel) => void;
    onCountChange: (count: number) => void;
  }
>(({ subcategory, onValidateShared, onSaveError, onCountChange }, ref) => {
  const isMobile = useMediaQuery({ query: QUERY_WIDTHS.md });
  const navigate = useNavigate();
  const notify = useNotify();

  const [cards, setCards] = useState<BulkCard[]>(() => [createBulkCard()]);
  const cardValuesRef = useRef<Record<string, TransactionBulkCardValues>>({});
  const cardApisRef = useRef<Record<string, TransactionBulkCardApi>>({});
  const [duplicatedCardIds, setDuplicatedCardIds] = useState<string[]>([]);
  const [limitViolation, setLimitViolation] =
    useState<ReturnType<typeof getAmountLimitViolation>>();
  const [confirmations, setConfirmations] = useState<AmountLimitConfirmations>(
    {},
  );

  useEffect(() => {
    onCountChange(cards.length);
  }, [cards.length, onCountChange]);

  const { mutate: mutateBulkCreate, isPending } = useTransactionBulkCreate(
    () => {
      notify(
        "success",
        cards.length > 1
          ? `${cards.length} transactions created successfully.`
          : "Transaction created successfully.",
      );
      navigate("/transactions");
    },
    (error) => {
      const errorsByIndex = getCardErrorsByIndex(error.fields);
      cards.forEach(({ id }, index) => {
        if (errorsByIndex[index]) {
          cardApisRef.current[id]?.setErrors(errorsByIndex[index]);
        }
      });
      onSaveError({
        ...error,
        fields: Object.fromEntries(
          Object.entries(error.fields || {}).filter(
            ([key]) => !key.startsWith("transactions."),
          ),
        ),
      });
    },
  );

  const handleRegisterCard = useCallback(
    (cardId: string, api?: TransactionBulkCardApi) => {
      if (api) {
        cardApisRef.current[cardId] = api;
      } else {
        delete cardApisRef.current[cardId];
      }
    },
    [],
  );

  const handleCardChange = useCallback(
    (cardId: string, values: TransactionBulkCardValues) => {
      cardValuesRef.current[cardId] = values;
      const descriptions = getCardDescriptions(cards, cardValuesRef.current);
      setDuplicatedCardIds((duplicatedIds) =>
        duplicatedIds.filter(
          (id) => getClashingDescriptionIds(descriptions, id).length,
        ),
      );
    },
    [cards],
  );

  const submit = async (confirmed: AmountLimitConfirmations = {}) => {
    const apis = cards.map(({ id }) => cardApisRef.current[id]);
    if (apis.some((api) => api?.isUploading())) {
      notify("error", "Please wait until the receipts finish uploading.");
      return;
    }

    const sharedValues = await onValidateShared();
    const validity = await Promise.all(apis.map((api) => api?.validate()));

    const descriptions = getCardDescriptions(cards, cardValuesRef.current);
    const clashingIds = [
      ...new Set(
        cards.flatMap(({ id }) => getClashingDescriptionIds(descriptions, id)),
      ),
    ];
    setDuplicatedCardIds(clashingIds);

    if (!sharedValues || validity.includes(false) || clashingIds.length) {
      return;
    }

    const transactions = apis.map((api) => api.getValues());
    const violations = transactions.map(({ amount }) =>
      getAmountLimitViolation(amount, subcategory, confirmed),
    );
    const violation =
      (["hardMax", "softMax", "softMin"] as const).find((type) =>
        violations.includes(type),
      ) || undefined;
    if (violation) {
      violations.forEach((cardViolation, index) => {
        if (cardViolation === violation) {
          apis[index].setErrors({ amount: LIMIT_ERRORS[violation] });
        }
      });
      setConfirmations(confirmed);
      setLimitViolation(violation);
      return;
    }

    mutateBulkCreate({ ...sharedValues, transactions });
  };

  useImperativeHandle(ref, () => ({ submit: () => submit() }));

  const addCard = () => setCards([...cards, createBulkCard()]);

  const removeCard = (cardId: string) => {
    delete cardValuesRef.current[cardId];
    setCards(cards.filter(({ id }) => id !== cardId));
    setDuplicatedCardIds((duplicatedIds) =>
      duplicatedIds.filter((id) => id !== cardId),
    );
  };

  return (
    <div>
      <FormRow
        top={isMobile ? "large" : "medium"}
        isRow
        isBetween
        className="items-center"
      >
        <div className="text-300 font-bold md:text-400">
          Transaction details
        </div>
        <Button
          type="button"
          variant="text"
          height="xsmall"
          title="Add lesson"
          leftIcon={<PlusSvg />}
          disabled={isPending || cards.length >= BULK_TRANSACTIONS_MAX}
          dataTestid="button_addBulkTransaction"
          onClick={addCard}
        />
      </FormRow>
      {/* Column widths are mirrored by the "Transaction information" block */}
      <div className="grid grid-cols-1 gap-4 pt-6 md:grid-cols-2 md:pt-8 xl:grid-cols-3">
        {cards.map((card, index) => (
          <TransactionBulkCard
            key={card.id}
            cardId={card.id}
            index={index}
            isDuplicated={duplicatedCardIds.includes(card.id)}
            isDescriptionRequired={cards.length > 1}
            canRemove={cards.length > 1}
            disabled={isPending}
            onChange={handleCardChange}
            onRemove={removeCard}
            onRegister={handleRegisterCard}
          />
        ))}
      </div>
      <SoftAmountLimitModal
        violation={limitViolation}
        softMaxMessage={subcategory?.softMaxAmountMessage}
        softMinMessage={subcategory?.softMinAmountMessage}
        onCancel={() => setLimitViolation(undefined)}
        onConfirm={(confirmed) => {
          setLimitViolation(undefined);
          submit({ ...confirmations, [confirmed]: true });
        }}
      />
      <HardMaxAmountModal
        show={limitViolation === "hardMax"}
        message={subcategory?.hardMaxAmountMessage}
        onClose={() => setLimitViolation(undefined)}
      />
    </div>
  );
});
